import { useEffect } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import { fetchFccRecommendedProductsThunk } from "~/domains/product/redux/fccRecommendationProducts/actions";
import {
  ProductRecommendationSection,
  ProductRecommendationTitle,
} from "~/features/saleRecommendations/components/ProductRecommendationSection";
import { ProductRecoType } from "~/features/saleRecommendations/models/ProductRecoType";
import { PurchaseTypeProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { type ProductData } from "~/domains/product/types/ProductData";

export interface FetchFCCRecommendedProductsProps {
  className?: string;
}

export interface FCCRecommendationsSectionProps {
  className?: string;
  loading: boolean;
  products: string[];
}

export const FetchFCCRecommendedProducts = (props: FetchFCCRecommendedProductsProps) => {
  const { loading, products } = useFCCRecommendedProducts();

  if (!loading && !products.length) {
    return null;
  }

  return <FCCRecommendationsSection loading={loading} products={products} {...props} />;
};

export const FCCRecommendationsSection = ({ loading, products, className }: FCCRecommendationsSectionProps) => {
  const { t } = useTranslation();

  const getTrackingAttributes = (product: ProductData) => {
    const rootTrackingAttributes = {
      [`tracking-aux-rfy-product-parent-marker`]: "true",
    };
    const linkProps = {
      [`tracking-aux-rfy-product-name`]: product.designation,
      [`tracking-aux-rfy-product-id`]: product.sapMaterialNumber,
      target: "_blank",
    };
    const buttonProps = {
      [`tracking-aux-rfy-add-to-cart-button`]: "true",
      reco_type: "rfy",
    };
    const gaLocation = "FCC product recommendations";
    return { rootTrackingAttributes, linkProps, buttonProps, gaLocation };
  };

  return (
    <PurchaseTypeProvider value={"rfy"}>
      <ProductRecommendationSection
        className={classNames(className)}
        title={<ProductRecommendationTitle>{t("feature.productRecommendations.title")}</ProductRecommendationTitle>}
        loading={loading}
        loadingLabel={t("feature.productRecommendations.loadingSection")}
        products={products}
        variant={ProductRecoType.fccGap}
        getTrackingAttributes={getTrackingAttributes}
      />
    </PurchaseTypeProvider>
  );
};

function useFCCRecommendedProducts() {
  const data = useSelector((state: RootState) => state.products.fccRecommendedProducts);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchFccRecommendedProductsThunk());
  }, [dispatch]);

  return {
    loading: data?.status === "loading",
    products: data?.productList.slice(0, 24).map((item) => item.code) ?? [], // As per SCHAEFF-33361, Max 24 products
  };
}
